import React, { useState } from "react"

import { Box } from "components/shared/box"
import { Search } from "components/shared/icons/search"

import { emailIsValid } from "utils/validate/email-is-valid"

import { Close } from "../icons/close"

type InputTextSize = "sm" | "md" | "lg"
type InputTextType = "text" | "email" | "password" | "number" | "search"

// styles
// https://www.figma.com/file/mUteT8dtwr6r8A1BhA34AC/Cryptobots-Web?node-id=1424%3A9628
export type InputTextProps = {
  placeholder?: string
  label?: string
  labelClassName?: string
  error?: string | boolean
  type: InputTextType
  value: string | number
  onChange?: (value: string) => void
  onBlur?: (value: string) => void
  onClear?: () => void
  disabled?: boolean
  size?: InputTextSize
  required?: boolean
  // todo addons
  leftAddon?: React.ReactElement
  rightAddon?: React.ReactElement
  className?: string
  classNameInput?: string
  inputColor?: string
  bgColor?: string
  borderColor?: string
  max?: number
  additionInputClasses?: string
  showClearButton?: boolean
  disableInnerInput?: boolean
}

export function InputText({
  type = "text",
  value = "",
  onChange,
  onBlur,
  disabled = false,
  required = false,
  placeholder = "",
  size = "md",
  className = "",
  classNameInput = "",
  inputColor = "white",
  bgColor = "bg-transparent",
  additionInputClasses = "",
  labelClassName = "",
  borderColor = "",
  max,
  label,
  error = "",
  showClearButton = false,
  onClear,
  disableInnerInput,
}: InputTextProps): React.ReactElement {
  const [isError, setIsError] = useState(false)

  const classNames = [
    `
        w-full
        h-full
        inline-flex
        rounded-box
        ${bgColor}
        border ${borderColor || "border-white/20"}
        text-[1rem] font-medium leading-[1.75rem] text-${inputColor} text-opacity-80

        focus:outline-none
        focus:border-white
        focus:text-${inputColor}
        focus-visible:outline-none
        focus-visible:text-${inputColor}
        focus-visible:border-white
    `,
    additionInputClasses,
  ]
  classNames.push(getInputTextSizeClasses(size))
  classNames.push(classNameInput)

  if (type === "search") {
    classNames.push("pl-12")
  }

  if (disabled) {
    classNames.push("disabled:opacity-50 disabled:select-none disabled:cursor-not-allowed")
  }

  if (isError || error) {
    classNames.push("outline outline-2 outline-pink-500")
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    value = event.target.value
    if (onChange) {
      onChange(event.target.value)

      validate(event.target.value)
    }
  }

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    onBlur?.(event.target.value)
    value = event.target.value
    validate(event.target.value)
  }

  const validate = (value: string) => {
    if (required && !value) {
      setIsError(true)
    } else if (type == "email" && !emailIsValid(value)) {
      setIsError(true)
    } else {
      setIsError(false)
    }
  }

  return (
    <label className={["inline-flex flex-col rounded-box relative", className].join(" ")}>
      {label &&
        (labelClassName ? (
          <span className={labelClassName}>{label}</span>
        ) : (
          <span className="text-white/80 mb-[0.25rem] text-sm">{label}</span>
        ))}
      <Box relative className="h-full">
        {type === "search" && <Search h="1.125rem" w="1.125rem" className="absolute top-4 left-4" />}
        <input
          maxLength={max}
          className={classNames.join(" ")}
          disabled={disabled || disableInnerInput}
          type={type}
          value={value}
          placeholder={placeholder}
          onChange={handleChange}
          onBlur={handleBlur}
        />
        {showClearButton && value && (
          <Close onClick={onClear} h="1.125rem" w="1.125rem" className="cursor-pointer absolute top-4 right-4" />
        )}
      </Box>
      {typeof error === "string" && (
        <span
          className={`text-primary/80 text-tiny md:text-sm absolute -bottom-[1.5rem] md:-bottom-[1.625rem] right-0`}
        >
          {error}
        </span>
      )}
    </label>
  )
}

const getInputTextSizeClasses = (size: InputTextSize) => {
  switch (size) {
    case "sm":
      return "py-1"
    case "md":
      return "py-2.5 px-4"
    case "lg":
      return "px-4 py-4 text-6"
    default:
      return ""
  }
}
